import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useGroupAdminAccess } from '~/hooks/useIsGroupAdmin';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin',
)({
  component: GroupAdminLayout,
});

/**
 * The guard for every `/admin/*` screen.
 *
 * Legacy gates the whole `Manage` nav and each screen under it on
 * `bp_is_item_admin()` (`class-bp-groups-component.php:908`, `:928`) and bounces
 * a refused caller to the group ROOT, not to the Dashboard tab — measured as
 * uid 2398, a mod of congregation 554: `GET /admin/` → 302
 * `/admin/member-tagging/` → 302 `/groups/dalhart-church-of-christ/`. None of
 * the four leaves (`group-avatar`, `member-tagging`, `reassign-students`,
 * `student-notice-msg`) had a guard of its own; this layout is the one place
 * they all get one.
 *
 * It navigates from the component rather than from `beforeLoad`: the answer
 * depends on `getGroupBySlug`, and web has no route-level data loading or
 * redirect anywhere.
 */
function GroupAdminLayout(): ReactElement | null {
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { isGroupAdmin, isResolved } = useGroupAdminAccess();
  const refused = isResolved && !isGroupAdmin;

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return <Outlet />;
}
